import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { navigate } from "gatsby";
import Link from "../TrailingSlashLink";

import { useWindowSize } from "../../hooks/useWindowSize";
import GlobalContext from "../../context/GlobalContext";
import { useAuth } from "../../context/AuthContext";
import { useUserPlan } from "../../context/UserPlanContext";
import {
  filterMainNavItemsForLoginPlans,
  shouldShowTenderBoqLeadNavAndFooter,
} from "../../utils/loginPlansAccess";
import { planTypeFromPlanId } from "../../utils/planUtils";
import Offcanvas from "../Offcanvas";
import NestedMenu from "../NestedMenu";
import { device } from "../../utils";
import Logo from "../Logo";
import { menuItems } from "./menuItems";

const OFFLINE_DASHBOARD_MESSAGE =
  "You need to be online to open Demo. Please check your internet connection.";


const SiteHeader = styled.header`
  .dropdown-toggle::after {
    opacity: 0;
  }

  padding: 15px 0 15px 0;
  position: absolute !important;
  top: 0;
  right: 0;
  width: 100%;
  z-index: 999;
  
  .navbar {
    min-height: 80px;
    max-height: 160px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
  }

  /* Mobile/tablet: keep logo and hamburger on one row – no wrap */
  @media (max-width: 1199px) {
    .navbar {
      flex-wrap: nowrap;
      display: flex;
      width: 100%;
    }
    /* Prevent .navbar-collapse from taking full width (Bootstrap uses flex-basis: 100% when collapsed, which pushes toggler to 2nd row) */
    .navbar-collapse {
      flex-basis: 0 !important;
      flex-grow: 0 !important;
      width: 0 !important;
      min-width: 0 !important;
      overflow: hidden !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .brand-logo {
      min-width: 0;
      flex-shrink: 1;
      margin-right: 8px;
    }
    .navbar-toggler.btn-close-off-canvas {
      flex-shrink: 0;
      margin-left: auto;
    }
  }
  
  .navbar-collapse {
    flex-grow: 1;
    display: flex;
    justify-content: center;
  }
  
  .navbar-nav-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: end;
    min-height: 80px;
  }
  
  .main-menu {
    margin: 0;
    align-items: center;
    display: flex;
    
    .nav-item {
      display: flex;
      align-items: center;
      
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: auto;
        min-height: 40px;
        padding: 8px 16px;
      }
    }
  }
  
  /* Ensure all action buttons in navbar have consistent height */
  .navbar-nav-wrapper .btn,
  .main-menu .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    height: auto;
    padding: 8px 16px;
  }

  /* Keep the full desktop navigation compact. The shared menu stylesheet uses
     large vertical padding and allows labels to wrap, which makes this header
     unnecessarily tall even when enough horizontal space is available. */
  @media (min-width: 1200px) {
    padding-top: 0;
    padding-bottom: 0;

    .navbar {
      min-height: 80px;
      max-height: none;
    }

    .brand-logo {
      flex: 0 0 clamp(180px, 14vw, 220px);
      width: clamp(180px, 14vw, 220px);
      margin-right: clamp(12px, 2vw, 28px);
    }

    .brand-logo > a {
      width: 100%;
    }

    .brand-logo > a > img {
      width: calc(100% - 24px) !important;
      height: auto !important;
    }

    .navbar-collapse,
    .navbar-nav-wrapper {
      min-width: 0;
    }

    .main-menu {
      flex-wrap: nowrap;
    }

    .main-menu > .nav-item > .nav-link {
      padding-top: 18px !important;
      padding-bottom: 18px !important;
      margin-left: clamp(8px, 1vw, 18px) !important;
      margin-right: clamp(8px, 1vw, 18px) !important;
      font-size: clamp(12px, 0.78vw, 13px);
      line-height: 1.25;
      white-space: nowrap;

      &:focus {
        outline: none;
        box-shadow: none;
      }

      &:focus-visible {
        outline: 2px solid #00b074;
        outline-offset: 3px;
        border-radius: 3px;
      }
    }

    .main-menu .nav-item .btn {
      padding-left: clamp(12px, 1vw, 16px);
      padding-right: clamp(12px, 1vw, 16px);
      font-size: 13px;
      white-space: nowrap;
    }

    .main-menu > .nav-item.ml-3 {
      margin-left: clamp(4px, 0.6vw, 12px) !important;
    }

    /* Logged-in public pages include both action buttons and every primary
       link. Reserve the logo's real width and tighten only this denser menu so
       it cannot overflow back across the brand at desktop display scaling. */
    &.site-header--authenticated {
      .brand-logo {
        flex-basis: 200px;
        width: 200px;
        margin-right: 12px;
      }

      .brand-logo > a > img {
        width: 100% !important;
        max-width: 200px;
      }

      .main-menu > .nav-item > .nav-link {
        margin-left: 10px !important;
        margin-right: 10px !important;

        &:focus-visible {
          outline: none;
          border-radius: 0;
          box-shadow: none;
        }

        &:focus-visible::before {
          transform: scaleX(1);
        }
      }

      .main-menu .nav-item .btn {
        padding-left: 12px;
        padding-right: 12px;
      }
    }
  }

  /* At 125% browser zoom, a 1920px display exposes roughly a 1536px CSS
     viewport. Keep the authenticated public navigation on one clean row at
     that effective width without changing the normal desktop presentation. */
  @media (min-width: 1200px) and (max-width: 1599.98px) {
    &.site-header--authenticated {
      .brand-logo {
        flex-basis: 180px;
        width: 180px;
        margin-right: 8px;
      }

      .brand-logo > a > img {
        max-width: 180px;
      }

      .main-menu > .nav-item > .nav-link {
        margin-left: 6px !important;
        margin-right: 6px !important;
        font-size: 12px;
      }

      .main-menu .nav-item .btn {
        padding-left: 10px;
        padding-right: 10px;
        font-size: 12px;
      }

      .main-menu > .nav-item.ml-3 {
        margin-left: 4px !important;
      }
    }
  }

  @media ${device.xl} {
    position: fixed !important;
    transition: 0.6s;
    &.scrolling {
      transform: translateY(-100%);
      transition: 0.6s;
    }
    &.reveal-header {
      transform: translateY(0%);
      box-shadow: 0 12px 34px -11px rgba(65, 62, 101, 0.1);
      z-index: 9999;
      background: ${({ dark, theme }) =>
        dark ? theme.colors.heading : theme.colors.light};
    }
  }

  /* Override SCSS toggle hiding – show hamburger below xl */
  .btn-close-off-canvas {
    display: block;
  }
  @media (min-width: 1200px) {
    .btn-close-off-canvas {
      display: none;
    }
  }
`;

const ToggleButton = styled.button`
  flex-shrink: 0;
  color: ${({ dark, theme }) =>
    dark ? theme.colors.lightShade : theme.colors.heading}!important;
  border-color: ${({ dark, theme }) =>
    dark ? theme.colors.lightShade : theme.colors.heading}!important;
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 10px 0;
  min-width: 0;
  flex-shrink: 1;

  a {
    display: flex;
    align-items: center;
    height: 100%;
    min-width: 0;

    img {
      max-height: 60px;
      max-width: 100%;
      width: auto;
      object-fit: contain;
    }
  }

  /* Tablet: 768px - 991px */
  @media (max-width: 991px) {
    padding: 8px 0;
    a img {
      max-width: 160px;
      max-height: 52px;
    }
  }

  /* Mobile */
  @media (max-width: 576px) {
    padding: 6px 0;
    a img {
      max-width: 130px;
      max-height: 44px;
    }
  }
`;


const Header = ({ publicPage = false }) => {
  const gContext = useContext(GlobalContext);
  const { logout, isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { loadPlanDetails, plans, planFilters } = useUserPlan();
  const userId = user?.id ?? user?.uid ?? user?.user_id ?? null;
  const showTenderBoqNavItems = shouldShowTenderBoqLeadNavAndFooter(
    isAuthenticated,
    user,
    authLoading
  );
  const headerMenuItems = filterMainNavItemsForLoginPlans(
    menuItems,
    showTenderBoqNavItems
  );
  const hasPlanFilters =
    Array.isArray(plans) &&
    plans.length > 0 &&
    ((Array.isArray(planFilters?.selectedcategories) && planFilters.selectedcategories.length > 0) ||
      (Array.isArray(planFilters?.selectedstates) && planFilters.selectedstates.length > 0));
  const [showScrolling, setShowScrolling] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [showOfflineToast, setShowOfflineToast] = useState(false);
  const offlineToastTimeoutRef = useRef(null);

  const displayOfflineToast = useCallback(() => {
    setShowOfflineToast(true);
    if (offlineToastTimeoutRef.current) {
      window.clearTimeout(offlineToastTimeoutRef.current);
    }
    offlineToastTimeoutRef.current = window.setTimeout(() => {
      setShowOfflineToast(false);
      offlineToastTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (offlineToastTimeoutRef.current) {
        window.clearTimeout(offlineToastTimeoutRef.current);
      }
    };
  }, []);

  const goToDashboardAfterPlanLoad = useCallback(() => {
    if (!userId) {
      navigate("/dashboard");
      return;
    }
    loadPlanDetails(userId)
      .then(() => navigate("/dashboard"))
      .catch(() => navigate("/dashboard"));
  }, [userId, loadPlanDetails]);

  const handleAuthenticatedDashboardClick = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        displayOfflineToast();
        return;
      }
      goToDashboardAfterPlanLoad();
    },
    [displayOfflineToast, goToDashboardAfterPlanLoad],
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      loadPlanDetails(userId);
    }
  }, [isAuthenticated, userId]);

  // Determine user plan type for menu filtering
  const getUserPlanType = () => {
    if (!isAuthenticated) {
      return "guest";
    }
    
    // Check plans from useUserPlan hook first, then fallback to user.plans
    const userPlans = (Array.isArray(plans) && plans.length > 0) 
      ? plans 
      : (Array.isArray(user?.plans) && user.plans.length > 0) 
        ? user.plans 
        : [];
    
    if (userPlans.length === 0) {
      return "guest"; // Guest user without any plans
    }
    
    const planTypes = userPlans.map(p => {
      const raw = p.plan_type ?? p.plantype ?? null;
      if (raw) return String(raw).toLowerCase().replace(/-/g, "_");
      return planTypeFromPlanId(p.plan_id ?? p.pId ?? p.pid ?? p.planId ?? null);
    }).filter(Boolean);
    
    if (planTypes.length === 0) {
      return "guest";
    }
    
    const hasFreemium = planTypes.includes("freemium");
    const hasEnterprise = planTypes.includes("enterprise") || planTypes.includes("pay_as_you_go");
    const hasInsights = planTypes.includes("insights");
    
    if (hasEnterprise || hasInsights) {
      return "enterprise";
    }
    if (hasFreemium && !hasEnterprise && !hasInsights) {
      return "freemium";
    }
    return "guest";
  };

  // Get filtered menu items based on plan type
  const getProfileMenuItems = () => {
    const planType = getUserPlanType();
    const allMenuItems = [
      { label: "My profile", to: "/me", key: "my-profile" },
      { label: "Change Password", to: "/dashboard-settings", key: "change-password" },
      { label: "Subscription Details", to: "/subscription-details", key: "subscription-details" },
      { label: "Upgrade Plan", to: "/plan-upgrade", key: "upgrade-plan" },
      { label: "Query Module", to: "/query-listing", key: "query-module" },
      { label: "Log Complaints", to: "/log-complaint", key: "log-complaints" },
      { label: "Make Bidding Profile", to: "/make-bidding-profile", key: "make-bidding-profile" },
      { label: "Generate Template", to: "/generate-template", key: "generate-template" },
      { label: "Feedback", to: "/feedback", key: "feedback" },
    ];

    if (planType === "guest") {
      // Guest users: Change Password, Upgrade Plan, Query Module, Log Complaints, Feedback
      return allMenuItems.filter(item => 
        ["my-profile", "change-password", "upgrade-plan", "query-module", "log-complaints", "feedback"].includes(item.key)
      );
    } else if (planType === "freemium") {
      // Freemium users: Change Password, Subscription Details, Upgrade Plan, Query Module, Log Complaints, Feedback
      return allMenuItems.filter(item => 
        ["my-profile", "change-password", "subscription-details", "upgrade-plan", "query-module", "log-complaints", "feedback"].includes(item.key)
      );
    } else {
      // Enterprise/360 users: All options
      return allMenuItems;
    }
  };

  const size = useWindowSize();

  useScrollPosition(({ prevPos, currPos }) => {
    if (currPos.y < 0) {
      setShowScrolling(true);
    } else {
      setShowScrolling(false);
    }
    if (currPos.y < -300) {
      setShowReveal(true);
    } else {
      setShowReveal(false);
    }
  });

  return (
    <>
      {showOfflineToast && (
        <div
          className="position-fixed p-3 bg-danger text-white rounded shadow-lg"
          style={{
            top: "24px",
            right: "24px",
            zIndex: 10000,
            maxWidth: "calc(100vw - 2rem)",
            wordWrap: "break-word",
          }}
          role="alert"
          aria-live="assertive"
        >
          <i className="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
          {OFFLINE_DASHBOARD_MESSAGE}
        </div>
      )}
      <SiteHeader
        className={`site-header site-header--sticky  mt-6 site-header--absolute py-7 py-xs-0 sticky-header ${
          gContext.header.bgClass
        } ${
          gContext.header.align === "left"
            ? "site-header--menu-left "
            : gContext.header.align === "right"
            ? "site-header--menu-right "
            : "site-header--menu-center "
        }
        ${gContext.header.theme === "dark" ? "dark-mode-texts" : " "} ${
          isAuthenticated && publicPage ? "site-header--authenticated" : ""
        } ${
          showScrolling ? "scrolling" : ""
        } ${
          gContext.header.reveal &&
          showReveal &&
          gContext.header.theme === "dark"
            ? "reveal-header bg-blackish-blue"
            : gContext.header.reveal && showReveal
            ? "reveal-header"
            : ""
        }`}
      >
        <Container
  fluid={gContext.header.isFluid}
  className={`mt-16 mt-xl-9 ${gContext.header.isFluid ? "pr-xl-9 pl-xl-9" : ""}`}
  style={{ position: "relative" }}
>


          <nav className="navbar site-navbar offcanvas-active navbar-expand-xl px-0 py-0">
            {/* <!-- Brand Logo--> */}
            <BrandLogo className="brand-logo">
              <Logo
                white={gContext.header.theme === "dark"}
                to={isAuthenticated ? "/dashboard" : "/"}
                onClick={
                  isAuthenticated
                    ? (e) => {
                        e.preventDefault();
                        handleAuthenticatedDashboardClick(e);
                      }
                    : undefined
                }
              />
            </BrandLogo>
            {/* {gContext.header.button != "profile" && ( */}

            <div className="collapse navbar-collapse">
              <div className="navbar-nav-wrapper">
                <ul className="navbar-nav main-menu d-none d-xl-flex">
                  {headerMenuItems
                    .filter((item) => isAuthenticated || item.name !== "dashboard")
                    .map(
                    (
                      { label, isExternal = false, name, items, ...rest },
                      index
                    ) => {
                      const hasSubItems = Array.isArray(items);
                      return (
                        <React.Fragment key={name + index}>
                          {hasSubItems ? (
                            <li className="nav-item dropdown" {...rest}>
                              <a
                                className="nav-link dropdown-toggle gr-toggle-arrow"
                                role="button"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                href="/#"
                                onClick={(e) => e.preventDefault()}
                              >
                                {label}
                                <i className="icon icon-small-down"></i>
                              </a>
                              <ul className="gr-menu-dropdown dropdown-menu ">
                                {items.map((subItem, indexSub) => {
                                  const hasInnerSubItems = Array.isArray(
                                    subItem.items
                                  );
                                  return (
                                    <React.Fragment
                                      key={subItem.name + indexSub}
                                    >
                                      {hasInnerSubItems ? (
                                        <li className="drop-menu-item dropdown">
                                          <a
                                            className="dropdown-toggle gr-toggle-arrow"
                                            role="button"
                                            data-toggle="dropdown"
                                            aria-expanded="false"
                                            aria-haspopup="true"
                                            href="/#"
                                            onClick={(e) => e.preventDefault()}
                                          >
                                            {subItem.label}
                                            <i className="icon icon-small-down"></i>
                                          </a>
                                          <ul className="gr-menu-dropdown dropdown-menu dropdown-left">
                                            {subItem.items.map(
                                              (itemInner, indexInnerMost) => (
                                                <li
                                                  className="drop-menu-item"
                                                  key={
                                                    itemInner.name +
                                                    indexInnerMost
                                                  }
                                                >
                                                  {itemInner.isExternal ? (
                                                    <a
                                                      href={`${itemInner.name}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                    >
                                                      {itemInner.label}
                                                    </a>
                                                  ) : itemInner.name === "modal-signin" ? (
                                                    <a
                                                      href="/#"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        gContext.toggleSignInModal();
                                                      }}
                                                    >
                                                      {itemInner.label}
                                                    </a>
                                                  ) : itemInner.name === "modal-signup" ? (
                                                    <a
                                                      href="/#"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        gContext.toggleSignUpModal();
                                                      }}
                                                    >
                                                      {itemInner.label}
                                                    </a>
                                                  ) : (
                                                    <Link
                                                      to={`/${itemInner.name}`}
                                                    >
                                                      {itemInner.label}
                                                    </Link>
                                                  )}
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </li>
                                      ) : (
                                        <li className="drop-menu-item">
                                          {subItem.isExternal ? (
                                            <a
                                              href={`${subItem.name}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {subItem.label}
                                            </a>
                                          ) : subItem.name === "modal-signin" ? (
                                            <a
                                              href="/#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                gContext.toggleSignInModal();
                                              }}
                                            >
                                              {subItem.label}
                                            </a>
                                          ) : subItem.name === "modal-signup" ? (
                                            <a
                                              href="/#"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                gContext.toggleSignUpModal();
                                              }}
                                            >
                                              {subItem.label}
                                            </a>
                                          ) : (
                                            <Link to={`/${subItem.name}`}>
                                              {subItem.label}
                                            </Link>
                                          )}
                                        </li>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </ul>
                            </li>
                          ) : (
                            <li className="nav-item" {...rest}>
                              {isExternal ? (
                                <a
                                  className="nav-link"
                                  href={`${name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {label}
                                </a>
                              ) : name === "dashboard" && !isAuthenticated ? (
                                <a
                                  className="nav-link"
                                  href="/dashboard"
                                  role="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    gContext.toggleAuthModal();
                                  }}
                                >
                                  {label}
                                </a>
                              ) : name === "dashboard" && isAuthenticated ? (
                                <Link
                                  className="btn btn-white mx-1 text-uppercase"
                                  to="/dashboard"
                                  activeClassName="active"
                                  role="button"
                                  aria-expanded="false"
                                  style={{cursor: "pointer", backgroundColor: "#00b074", color: "white"}}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleAuthenticatedDashboardClick(e);
                                  }}
                                >
                                  <i className="topbar__btn-icon fas fa-tachometer-alt mr-1 text-uppercase"></i>
                                  {label}
                                </Link>
                              ) : (
                                <Link
                                  className="nav-link"
                                  to={`/${name}`}
                                  activeClassName="active"
                                  role="button"
                                  aria-expanded="false"
                                >
                                  {label}
                                </Link>
                              )}
                            </li>
                          )}
                        </React.Fragment>
                      );
                    }
                  )}
                  {/* Blog – visible when logged out only */}
                  {!isAuthenticated && (
                    <li className="nav-item">
                      <Link
                        className="nav-link"
                        to="/blog"
                        activeClassName="active"
                        role="button"
                        aria-expanded="false"
                      >
                        Blog
                      </Link>
                    </li>
                  )}
                  {/* Pricing / Subscription – public */}
                  <li className="nav-item ml-3">
                    <Link
                      to="/pricing"
                      className="btn btn-white mx-1 text-uppercase"
                      style={{cursor: "pointer", backgroundColor: "rgb(15, 117, 188)", color: "white"}}
                    >
                      <i className="topbar__btn-icon fas fa-credit-card mr-1 text-uppercase"></i>
                      Pricing
                    </Link>
                  </li>
                 
                </ul>
              </div>
            </div>
            {/* )} */}

            {/* Commented out login/signup buttons - now using menu structure */}
            {/* {gContext.header.button === "account" && (
              <div className="header-btns header-btn-devider ml-auto pr-2 ml-lg-6 d-none d-xs-flex">
                <a
                  className="btn btn-transparent text-uppercase font-size-3 heading-default-color focus-reset"
                  href="/#"
                  onClick={(e) => {
                    e.preventDefault();
                    gContext.toggleSignInModal();
                  }}
                >
                  Log In
                </a>
                <a
                  className={`btn btn-${gContext.header.variant} text-uppercase font-size-3`}
                  href="/#"
                  onClick={(e) => {
                    e.preventDefault();
                    gContext.toggleSignUpModal();
                  }}
                >
                  Sign Up
                </a>
              </div>
            )} */}

            <ToggleButton
              className={`navbar-toggler btn-close-off-canvas ml-auto ${
                gContext.visibleOffCanvas ? "collapsed" : ""
              }`}
              type="button"
              data-toggle="collapse"
              data-target="#mobile-menu"
              aria-controls="mobile-menu"
              aria-expanded="false"
              aria-label="Toggle navigation"
              onClick={gContext.toggleOffCanvas}
              dark={gContext.header.theme === "dark" ? 1 : 0}
            >
              {/* <i className="icon icon-simple-remove icon-close"></i> */}
              <i className="icon icon-menu-34 icon-burger d-block"></i>
            </ToggleButton>
          </nav>
        </Container>
      </SiteHeader>
      <Offcanvas
        show={gContext.visibleOffCanvas}
        onHideOffcanvas={gContext.toggleOffCanvas}
      >
        <NestedMenu
          onAuthenticatedDashboardClick={
            isAuthenticated ? handleAuthenticatedDashboardClick : undefined
          }
          menuItems={[
            ...headerMenuItems.filter(
              (item) => isAuthenticated || item.name !== "dashboard"
            ),
            ...(!isAuthenticated ? [{ name: "blog", label: "Blog" }] : []),
            { name: "pricing", label: "Pricing" },
            ...(isAuthenticated
              ? [
                  {
                    name: "profile-section",
                    label: "Profile",
                    items: getProfileMenuItems().map(item => ({
                      name: item.to.replace(/^\//, ""), // Remove leading slash
                      label: item.label,
                    })),
                  },
                ]
              : []),
          ]}
        />
        {isAuthenticated && (
          <div className="mt-3 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm w-100 text-uppercase print-hide-logout"
              onClick={() => {
                logout();
                if (gContext.visibleOffCanvas) {
                  gContext.toggleOffCanvas();
                }
              }}
            >
              <i className="fas fa-sign-out-alt mr-2"></i>
              Logout
            </button>
          </div>
        )}
      </Offcanvas>
    </>
  );
};
export default Header;
